<template>
  <form
    class="row py-4"
    @submit.prevent="save"
  >
    <div class="col-12 col-md-8 border-right">
      <div class="form-group">
        <label>Legal Name</label>
        <multiselect
          v-model="lei"
          :options="leis"
          :internal-search="false"
          :allow-empty="false"
          :searchable="true"
          :multiple="false"
          :show-labels="false"
          class="multiselect"
          style="cursor:pointer"
          select-label="Select"
          label="LegalName"
          track-by="LEI"
          @search-change="delayGetLei"
        />

        <form-control-error :errors="(error && error.errors.parent_account_id) ? error.errors.parent_account_id : []" />
      </div>

      <form-control
        v-model="data.lei"
        placeholder="LEI"
        title="LEI"
        disabled
      />

      <form-control
        v-model="data.name"
        :errors="error ? error.errors.name : []"
        placeholder="Start typing..."
        title="Account Name"
      />

      <form-control
        v-model="data.address.address1"
        :errors="error && error.errors.address ? error.errors.address.address1 : []"
        placeholder="Address 1"
        title="Address 1"
      />

      <form-control
        v-model="data.address.address2"
        :errors="error && error.errors.address ? error.errors.address.address2 : []"
        placeholder="Address 2"
        title="Address 2"
      />

      <form-control
        v-model="data.address.city"
        :errors="error && error.errors.address ? error.errors.address.city : []"
        placeholder="City"
        title="City"
      />

      <form-control
        v-model="data.address.state"
        :errors="error && error.errors.address ? error.errors.address.state : []"
        placeholder="State"
        title="State"
      />

      <div class="form-group">
        <label>Country</label>
        <multiselect
          v-model="data.address.country"
          :options="countries"
          :show-labels="false"
          :class="{
            'is-invalid': ((error && error.errors.address) ? error.errors.address.country_id : []).length > 0
          }"
          placeholder="Type to search"
          track-by="id"
          label="name"
          @select="(val) => data.address.country_id = val.id"
        >
          <span slot="noResult">
            No elements found.
          </span>
          <span
            slot="placeholder"
            class="multiselect__placeholder"
          >
            Type to search
          </span>
        </multiselect>
      </div>

      <form-control
        v-model="data.address.postal_code"
        :errors="error && error.errors.address ? error.errors.address.postal_code : []"
        placeholder="Postal Code"
        title="Postal Code"
      />

      <form-control
        v-model="data.address.phone"
        :errors="error && error.errors.address ? error.errors.address.phone : []"
        placeholder="Phone"
        title="Phone"
      />

      <div class="form-group">
        <label>Parent Account</label>
        <autocomplete
          ref="first"
          :input-class="['form-control']"
          :initial-value="data.parent_account ? data.parent_account.id : null"
          :initial-display="data.parent_account ? data.parent_account.name : null"
          class="align-self-center"
          placeholder="Select an account...  "
          source="/accounts/search?q="
          results-property="data"
          @selected="selectedParent"
          @clear="selectedParent"
        />

        <form-control-error :errors="(error && error.errors.parent_account_id) ? error.errors.parent_account_id : []" />
      </div>

      <form-control
        v-model="data.website"
        :errors="error ? error.errors.website : []"
        placeholder="Start typing..."
        title="Website"
      />

      <div class="form-group">
        <label>Account Type</label>
        <multiselect
          v-model="data.account_type"
          :options="accountTypes"
          :allow-empty="false"
          :show-labels="false"
          :class="{
            'is-invalid': ((error && error.errors['account_type_id']) ? error.errors['account_type_id'] : []).length > 0
          }"
          :searchable="false"
          placeholder="Select account type"
          class="multiselect pb-0"
          style="cursor:pointer"
          select-label="Select"
          label="name"
          track-by="id"
          @input="(option, id) => { data.account_type_id = option.id }"
        >
          <form-control-error :errors="(error && error.errors.parent_account_id) ? error.errors.parent_account_id : []" />
        </multiselect>
      </div>

      <div class="form-group pt-4 border-top">
        <label>Sub Accounts</label>
        <quick-selector
          :options="[]"
          :selected="data.children"
          v-model="data.children"
          class="quick-selection--authors my-1"
          placeholder="Search for account and hit enter to add"
        >
          <template
            slot="option"
            slot-scope="scope"
            v-if="scope.option"
          >
            <span>{{ scope.option.name }}</span>
          </template>
        </quick-selector>

        <div class="tag-input">
          <div class="tag-input__list">
            <div
              v-for="(account, index) in data.children"
              :key="account.id"
              class="tag-input__list__item"
            >
              <span>{{ account.name }}</span>
              <span @click="data.children.splice(index, 1)">
                <icon
                  icon="remove-tag"
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="data.id"
        class="py-3 d-flex align-items-center border-top"
      >
        <button class="ml-auto btn btn-outline-primary">
          <span>
            Create New Sub Account
          </span>
          <icon
            icon="edit"
            class="ml-2"
          />
        </button>
      </div>

      <div class="py-3 d-none d-md-flex align-items-center border-top">
        <button
          v-if="!$route.name.includes('create') && $login.can('accounts.delete')"
          type="button"
          class="mr-auto btn  btn-outline-danger"
          @click.prevent="$modal.show('confirm-delete')"
        >
          Delete Account
        </button>

        <button
          class="btn btn-primary "
        >
          Save
        </button>
      </div>
    </div>

    <div class="col-12 col-md-4">
      <div class="mb-3 d-flex flex-column">
        <label>MFID II</label>
        <button
          @click="() => data.is_mifid = true"
          :class="{'btn-outline-primary': data.is_mifid }"
          class="d-flex w-50 btn align-items-center justify-content-center"
        >
          Compliant
        </button>
        <button
          @click="() => data.is_mifid = false"
          :class="{'btn-outline-primary': !data.is_mifid }"
          class="d-flex w-50 btn align-items-center justify-content-center"
        >
          Not Compliant
        </button>
      </div>

      <div>
        <label class="d-block">Account Logo</label>


        <img
          v-if="data.logo_url"
          class="mb-2"
          :src="data.logo_url"
          :alt="data.name"
          style="width: 100px"
          width="d-block"
        >

        <FileUpload
          :headers="headers()"
          :accept="['image/png', 'image/jpeg']"
          :action="url() + 'accounts/logo'"
          @input="logoUploaded"
          :value="data.logo_url ? [{name: data.logo_url, url: data.logo_url }] : []"
        />
      </div>

      <div class="mt-3 py-3 d-flex d-md-none align-items-center save-account-footer justify-content-end border-top">
        <button
          v-if="!$route.name.includes('create')"
          type="button"
          class="mr-auto btn  btn-outline-danger"
          @click="$router.go(-1)"
        >
          Delete Account
        </button>

        <button
          class="btn btn-primary "
        >
          Save
        </button>
      </div>
    </div>
    <confirm
      v-if="data"
      @confirmed="deleteAccount"
      :name="data.name"
    />
  </form>
</template>

<script>
import QuickSelector from '@/components/QuickSelector'
import Autocomplete from '@/components/Autocomplete'
import Confirm from '@/components/Confirm'
import {successNotification} from '@/notifications'
import {debounce, getOnce} from '@/helpers'
import {FileUpload} from '@frontend/components/src/FileUpload'

export default {
  components: {
    QuickSelector,
    Autocomplete,
    Confirm,
    FileUpload,
  },
  props: {
    value: {
      required: true,
      type: Object,
    },
  },

  watch: {
    value: {
      deep: true,
      handler () {
        this.data = this.value
        this.setLeiFromData()
      },
    },

    lei () {
      this.getLegalInfo()
    },
  },

  data () {
    return {
      lei: null,
      data: null,
      error: null,
      logo: [],
      accountTypes: [],
      countries: [],
      leis: [],
    }
  },

  beforeMount () {
    this.data = this.value
    this.setLeiFromData()
    this.getAccountTypes()
    getOnce('countries').then((r) => this.countries = r.data.data)
  },

  methods: {
    setLeiFromData () {
      this.lei = {
        LegalName: this.value.legal_name,
        LEI: this.value.lei,
      }
    },
    save () {
      const method = this.data.id ? 'put' : 'post'
      const url = this.data.id ? `/accounts/${this.data.id}` : '/accounts'
      this.$http[method](url, this.data)
          .then(this.success)
          .catch(this.failure)
    },

    getLegalInfo () {
      if (this.data.lei === this.lei.LEI) {
        return
      }
      this.data.legal_name = this.lei.LegalName
      this.data.lei = this.lei.LEI
      this.data.name = this.lei.LegalName
      const request = require('request')
      request(`https://leilookup.gleif.org/api/v2/leirecords?lei=${this.lei.LEI}`, this.gotLegalInfo)
    },

    gotLegalInfo (err, response, body) {
      body = JSON.parse(body)
      if (body.length > 0) {
        const entity = body[0].Entity
        let address2 = ''
        this.data.address.address1 = entity.LegalAddress.FirstAddressLine['$'] || null
        if (entity.LegalAddress.AdditionalAddressLine) {
          for (const line of entity.LegalAddress.AdditionalAddressLine) {
            address2 += line['$']
          }
        }
        this.data.address.address2 = address2
        this.data.address.city = entity.LegalAddress.City['$']
        this.data.address.postal_code = entity.LegalAddress.PostalCode['$']

        const country = this.countries.find((e) => e.alpha_2_code === entity.LegalAddress.Country['$'])

        this.data.address.country = country
        this.data.address.country_id = country.id
      }
    },

    delayGetLei: debounce(function (query) {
      if (query.length >= 5) {
        this.$http.get(`https://api.leilex.com/API/LEI/AutoComplete/?filterType=Name&query=${query}`).then((response) => {
          this.leis = response.data
        })
      }
    }, 200),

    success (response) {
      this.$notify(successNotification('Saved account successfully'))
      this.$emit('save-success', response)
    },

    failure (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        this.error = error.response.data
      }
      this.$emit('save-fail', error)
    },

    getAccountTypes () {
      this.$http.get('/accounts/types').then((response) => {
        this.accountTypes = response.data.data
      })
    },
    selectedLei (selection) {
      if (selection) {
        this.lei = selection.object
      } else {
        this.lei = null
      }
    },

    selectedParent (selection) {
      this.data.parent_account_id = null
      this.data.parent_account = null
      if (selection) {
        this.data.parent_account = selection.object
        this.data.parent_account_id = selection.object.id
      }
    },

    headers () {
      return {
        Authorization: 'Bearer ' + this.$login.getToken(),
      }
    },

    handlePictureCardPreview (file) {
      this.dialogImageUrl = file.url
      this.dialogVisible = true
    },

    url () {
      return process.env.VUE_APP_API_ENDPOINT
    },

    logoUploaded (files) {
      const data = files[0]
      this.data.logo = data ? data.name : null
      this.data.logo_url = data ? data.url : null
    },

    deleteAccount () {
      this.$http.delete(`/accounts/${this.data.id}`)
          .then(() => {
            this.$notify(successNotification(`Removed account ${this.data.name}`))
            this.$router.replace({name: 'accounts', hash: '#fresh'})
          })
    },
  },
}
</script>

<style lang="scss">
</style>
